
import { motion } from "framer-motion";
import { 
  Menu, 
  ShoppingCart, 
  LayoutDashboard,
  User,
  ChevronRight
} from "lucide-react";

/**
 * MobileMenuNav Component
 * 
 * Main navigation links for the mobile menu.
 * Phase 2.2: Enhanced with larger touch targets, icon backgrounds, and smooth animations
 * 
 * Features:
 * - 56px minimum height for better touch accessibility
 * - Icon backgrounds with color-coded theming
 * - Smooth slide animation on hover
 * - Animated chevrons indicating clickability
 * - Wiggle animation on icon hover
 * 
 * @param {MobileMenuNavProps} props - Component props
 * @returns {JSX.Element} Navigation items list
 */

const navigation = [
  { name: 'Home', href: '/', icon: Menu },
  { name: 'Products', href: '/products', icon: ShoppingCart },
  { name: 'Categories', href: '/categories', icon: LayoutDashboard },
  { name: 'About', href: '/about', icon: User },
  { name: 'Contact', href: '/contact', icon: User },
];

const itemVariants = {
  hidden: { x: -20, opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
  },
};

interface MobileMenuNavProps {
  handleLinkClick: (path: string) => void;
}

const MobileMenuNav = ({ handleLinkClick }: MobileMenuNavProps) => {
  return (
    <>
      {navigation.map((item) => {
        const Icon = item.icon;
        return (
          <motion.div key={item.name} variants={itemVariants}>
            <motion.button
              onClick={() => handleLinkClick(item.href)}
              whileHover={{ x: 4 }}
              whileTap={{ scale: 0.98 }}
              className="w-full text-left flex justify-between items-center min-h-[48px] px-3 py-2 rounded-2xl text-base font-medium text-muted-foreground hover:text-foreground hover:bg-accent/60 border border-transparent hover:border-border/40 transition-all duration-300 group hover:shadow-sm"
            >
              <div className="flex items-center gap-3">
                <motion.div
                  whileHover={{ rotate: [0, -10, 10, 0] }}
                  transition={{ duration: 0.3 }}
                  className="p-1.5 rounded-xl bg-primary/10 group-hover:bg-primary/20 transition-colors duration-300"
                >
                  <Icon className="h-5 w-5 text-primary" />
                </motion.div>
                <span className="font-semibold">{item.name}</span>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground/40 group-hover:text-primary transition-all duration-300" />
            </motion.button>
          </motion.div>
        );
      })}
    </>
  );
};

export default MobileMenuNav;
